// The Process window of a batch: a view of the page's run (useProcessRun /
// processEvents.js), one screen per phase. Every number comes from the
// backend: the batch detail before the run (ready_breakdown,
// missing_breakdown, counts), the stream during it, GET /:batchId after it.
import React from 'react'
import PropTypes from 'prop-types'
import { XClose } from '@untitled-ui/icons-react'
import { MyButton, MyModal } from '@interstellar-component'
import { FILE_KIND_LABEL } from '@src/services/consentSettings'
import { formatDateTime } from '@src/services/Helper'
import MyMemberStatusChip from '@pages/Company/components/MyMemberStatusChip'
import StatusChip from './StatusChip'
import Notice from './Notice'
import ConsentChanges, { ektpChanged } from './ConsentChanges'
import { FundingChip } from './FundingChips'
import { isLivePhase } from '../processEvents'
import {
  BATCH_STATUS,
  BLOCKING_REASON_LABEL,
  breakdownText,
  chipOf,
  consentSettingsChanged,
  outcomeNotes,
  plural,
  pluralVerb,
} from '../constants'

// "1 row gets" / "2 rows get"
const rowsDo = (n, verbOne, verbMany) => pluralVerb(n, 'row', verbOne, verbMany)

const TITLES = {
  confirm: 'Process ready rows',
  starting: 'Starting…',
  streaming: 'Processing',
  watching: 'Processing',
  done: 'Processing finished',
  stopped: 'Processing stopped',
  failed: 'Processing stopped unexpectedly',
  refused: 'Processing did not start',
}

const WATCH_TEXT = {
  already_processing:
    'This batch is already being processed (in another tab, or by another user). This window follows it until it ends.',
  taken_over: 'Another Process run took this batch over. This window follows it until it ends.',
  connection_lost:
    'The progress stream was cut, but processing goes on on the server. This window checks the batch every few seconds until it ends.',
}

function Checking({ text }) {
  return (
    <div role="status" className="flex items-center gap-3 py-6 text-sm text-gray-600">
      <span className="size-5 shrink-0 animate-spin rounded-full border-2 border-brand/200 border-t-brand/600" aria-hidden />
      {text}
    </div>
  )
}
Checking.propTypes = { text: PropTypes.node.isRequired }

function ConsentChangedBlock({ changes, resyncAllowed, busy, onResync, onUploadNew }) {
  return (
    <div className="mt-2 flex flex-col gap-2">
      {changes?.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <p className="font-medium">Changed since upload:</p>
          <ConsentChanges changes={changes} />
        </div>
      )}
      {resyncAllowed ? (
        <>
          <p>
            Apply the current settings to this batch: every row is checked again against them
            {ektpChanged(changes)
              ? '. If HR no longer supplies the e-KTP, the e-KTP data and photos already in this batch are removed. This cannot be undone.'
              : '.'}
          </p>
          <div>
            <MyButton type="button" color="primary" variant="outlined" size="sm" disabled={busy} onClick={onResync}>
              {busy ? 'Applying…' : 'Apply current settings'}
            </MyButton>
          </div>
        </>
      ) : (
        <>
          <p>This batch was uploaded without e-KTP data, so it cannot take the new settings: upload the file again with a new template.</p>
          <div>
            <MyButton type="button" color="secondary" variant="outlined" size="sm" onClick={onUploadNew}>
              Upload a new batch
            </MyButton>
          </div>
        </>
      )}
    </div>
  )
}
ConsentChangedBlock.propTypes = {
  changes: PropTypes.array,
  resyncAllowed: PropTypes.bool.isRequired,
  busy: PropTypes.bool,
  onResync: PropTypes.func.isRequired,
  onUploadNew: PropTypes.func.isRequired,
}

/**
 * Files the page's file grid holds and has not saved (`files`: { unsaved,
 * uploading }). Process takes each row's SAVED files: a replacement not saved
 * yet would be ignored (its row processed with the old file) and fail to save
 * afterwards, so Process waits until they are saved or discarded.
 */
function filesHoldProcess(files) {
  return Boolean(files?.uploading) || (files?.unsaved || 0) > 0
}

function UnsavedFilesNotice({ files }) {
  if (!filesHoldProcess(files)) return null
  return (
    <Notice tone="warning" title="Save your files first">
      <p>
        {files.uploading
          ? 'Files are still being checked or saved in Complete files.'
          : `${pluralVerb(files.unsaved, 'file', 'you added is', 'you added are')} not saved yet.`}{' '}
        Processing takes the files already saved on each row, so it waits until yours are saved or discarded.
      </p>
    </Notice>
  )
}
UnsavedFilesNotice.propTypes = {
  files: PropTypes.shape({ unsaved: PropTypes.number, uploading: PropTypes.bool }),
}

function ConfirmStep({ batch, checking, recheckFailed, busy, files, onResync, onUploadNew }) {
  if (checking) return <Checking text="Checking the batch…" />
  const counts = batch.counts || {}
  const ready = counts.ready || 0
  const direct = batch.ready_breakdown?.direct || 0
  const invite = batch.ready_breakdown?.invite || 0
  const incomplete = counts.incomplete || 0
  const failed = counts.failed || 0
  const missing = breakdownText(batch.missing_breakdown, FILE_KIND_LABEL)
  const reasons = batch.blocking_reasons || []

  return (
    <>
      {recheckFailed && (
        <Notice tone="warning" title="The batch could not be checked again">
          <p>The numbers below may be out of date. Processing checks every row again anyway.</p>
        </Notice>
      )}
      <UnsavedFilesNotice files={files} />
      {!batch.can_process ? (
        <Notice tone="warning" title="This batch cannot be processed now">
          <ul className="list-disc pl-5">
            {reasons.map((code) => (
              <li key={code}>{BLOCKING_REASON_LABEL[code] || code}</li>
            ))}
          </ul>
          {consentSettingsChanged(batch) && (
            <ConsentChangedBlock
              changes={batch.consent_changes}
              resyncAllowed={batch.resync_allowed !== false}
              busy={busy}
              onResync={onResync}
              onUploadNew={onUploadNew}
            />
          )}
        </Notice>
      ) : (
        <>
          <p className="text-base font-semibold text-gray-900">Process {plural(ready, 'ready row')}?</p>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-gray-700">
            {invite > 0 && <li>{rowsDo(invite, 'gets', 'get')} a consent invitation email now.</li>}
            {direct > 0 && <li>{rowsDo(direct, 'goes', 'go')} straight to CLIK, without an invitation.</li>}
            {incomplete > 0 && (
              <li>
                {rowsDo(incomplete, 'is', 'are')} skipped: still missing files{missing ? ` (${missing})` : ''}.
              </li>
            )}
            {failed > 0 && (
              <li>
                {plural(failed, 'failed row')} {failed === 1 ? 'is' : 'are'} not included: use Retry failed rows first.
              </li>
            )}
          </ul>
          <Notice tone="info" title="Each enquiry is charged when it is created">
            <p>
              As a single New Enquiry: company quota first, then saldo. If neither covers it, the enquiry waits as
              Pending Payment and is paid automatically, in upload order, after a top-up.
            </p>
          </Notice>
          {direct > 0 && (
            <Notice tone="warning" title="Direct to CLIK">
              <p>
                Direct rows are sent to CLIK right away, with no consent step for the person: the Terms and condition
                document from HR is the consent record. The charge stays even if the CLIK result fails.
              </p>
            </Notice>
          )}
          <p className="text-xs text-gray-500">
            Processing stops by itself if the company can no longer be charged or is no longer active; the rows not
            reached stay ready. You can hide this window: processing goes on on the server.
          </p>
        </>
      )}
    </>
  )
}
ConfirmStep.propTypes = {
  batch: PropTypes.object.isRequired,
  checking: PropTypes.bool,
  recheckFailed: PropTypes.bool,
  busy: PropTypes.bool,
  files: PropTypes.object,
  onResync: PropTypes.func.isRequired,
  onUploadNew: PropTypes.func.isRequired,
}

function RunProgress({ current, total }) {
  const percent = total ? Math.min(100, Math.round((current / total) * 100)) : 0
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between text-sm text-gray-700">
        <span>
          {current} of {plural(total, 'row')} processed
        </span>
        <span>{percent}%</span>
      </div>
      <div
        role="progressbar"
        aria-label="Rows processed"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={current}
        className="h-2 w-full overflow-hidden rounded bg-gray-200"
      >
        <div className="h-2 rounded bg-brand/600 transition-all" style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}
RunProgress.propTypes = { current: PropTypes.number.isRequired, total: PropTypes.number.isRequired }

const FUNDING_SHOWN = ['funded', 'pending_payment', 'parked', 'canceled']

/** Counts of the run: the stream's tally while it runs, the backend's summary at its end. */
function RunCounts({ counts, direct }) {
  const cells = [
    ['Created', counts.created],
    ['Failed', counts.failed],
    ['Skipped', counts.skipped],
  ]
  const funding = FUNDING_SHOWN.filter((bucket) => counts[bucket] > 0)
  return (
    <div className="flex flex-col gap-2">
      <dl className="grid grid-cols-3 gap-3">
        {cells.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-gray-200 px-3 py-2">
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="text-lg font-semibold text-gray-900">{value || 0}</dd>
          </div>
        ))}
      </dl>
      {funding.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500">Created:</span>
          {funding.map((bucket) => (
            <FundingChip key={bucket} bucket={bucket} count={counts[bucket]} />
          ))}
        </div>
      )}
      {(direct || counts.sent_to_clik > 0 || counts.dispatch_deferred > 0) && (
        <p className="text-xs text-gray-600">
          Direct to CLIK: {counts.sent_to_clik || 0} sent · {counts.dispatch_deferred || 0} sent automatically within a
          minute
        </p>
      )}
    </div>
  )
}
RunCounts.propTypes = { counts: PropTypes.object.isRequired, direct: PropTypes.bool }

function LogLine({ entry }) {
  const where = `Row ${entry.row_no ?? '?'}`
  if (entry.status === 'created') {
    const notes = outcomeNotes(entry.error)
    return (
      <li className="flex flex-col gap-1 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{where}</span>
          {entry.reference_number && <span className="text-sm text-gray-700">{entry.reference_number}</span>}
          {entry.funding && <FundingChip bucket={entry.funding} />}
          {entry.dispatched === true && <StatusChip label="Sent to CLIK" color="primary" />}
        </div>
        {notes.map((note, i) => (
          <span key={i} className={`text-xs ${note.tone === 'error' ? 'text-error/700' : 'text-gray-600'}`}>
            {note.label}
          </span>
        ))}
      </li>
    )
  }
  const failed = entry.status === 'failed'
  return (
    <li className="flex flex-col gap-1 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-900">{where}</span>
        <StatusChip label={failed ? 'Failed' : 'Skipped'} color={failed ? 'error' : 'gray'} />
      </div>
      {entry.error && <span className={`text-xs ${failed ? 'text-error/700' : 'text-gray-600'}`}>{entry.error}</span>}
    </li>
  )
}
LogLine.propTypes = { entry: PropTypes.object.isRequired }
// The log grows by one line per row: the lines already there do not render again.
const MemoLogLine = React.memo(LogLine)

function RunLog({ log }) {
  if (!log?.length) return null
  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium text-gray-500">Rows, latest first</p>
      <ul
        aria-label="Processed rows"
        className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200"
      >
        {[...log].reverse().map((entry) => (
          <MemoLogLine key={`${entry.row_id || entry.row_no}-${entry.status}`} entry={entry} />
        ))}
      </ul>
    </div>
  )
}
RunLog.propTypes = { log: PropTypes.array }

/** Where the batch is after the run (the backend's result / stop summary). */
function AfterRun({ summary }) {
  const lines = []
  if (summary.status === 'completed') lines.push('The batch is completed: no row is left to process.')
  if (summary.remaining_incomplete > 0) {
    lines.push(`${rowsDo(summary.remaining_incomplete, 'still waits', 'still wait')} for files.`)
  }
  if (summary.remaining_ready > 0) {
    lines.push(`${rowsDo(summary.remaining_ready, 'is', 'are')} ready and not processed yet.`)
  }
  if (summary.pending_payment > 0) {
    lines.push(
      'Pending Payment enquiries are paid automatically in upload order after a top-up, a few per minute, so they do not all clear at once.'
    )
  }
  if (summary.failed > 0) {
    lines.push('Failed rows have no enquiry. Retry failed rows checks them again; some may turn invalid, with the reason.')
  }
  if (!lines.length) return null
  return (
    <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-gray-700">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  )
}
AfterRun.propTypes = { summary: PropTypes.object.isRequired }

const REFUSAL_TITLES = {
  consent_config_changed: 'The consent settings changed',
  no_subscription: 'The company cannot be charged',
  inactive: 'The company is not active',
  billing_unavailable: 'Billing cannot be reached',
  no_create_permission: 'No permission',
  permission: 'No permission',
  session: 'Session expired',
  network: 'Connection failed',
  not_found: 'Batch not found',
}

function RefusedStep({ refusal, busy, onResync, onUploadNew }) {
  const { code, message, extras = {} } = refusal
  return (
    <Notice
      tone={code === 'consent_config_changed' ? 'warning' : 'error'}
      title={REFUSAL_TITLES[code] || 'Nothing was processed'}
    >
      <p>{message}</p>
      {code === 'inactive' && extras.member_status && (
        <div className="mt-1 flex items-center gap-2">
          <span>Member status:</span>
          <MyMemberStatusChip status={extras.member_status} />
        </div>
      )}
      {code === 'consent_config_changed' && (
        <ConsentChangedBlock
          changes={extras.consent_changes}
          resyncAllowed={extras.resync_allowed !== false}
          busy={busy}
          onResync={onResync}
          onUploadNew={onUploadNew}
        />
      )}
      {code === 'no_subscription' && <p>Nothing was processed. Sort out the company&apos;s billing, then process again.</p>}
    </Notice>
  )
}
RefusedStep.propTypes = {
  refusal: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  onResync: PropTypes.func.isRequired,
  onUploadNew: PropTypes.func.isRequired,
}

function SettledStep({ settled }) {
  const counts = settled.counts || {}
  return (
    <>
      {settled.stale ? (
        <Notice tone="warning" title="Processing stopped before it finished">
          <p>Its ready rows can be processed again: press Continue processing.</p>
        </Notice>
      ) : (
        <Notice tone="success" title={`The batch is ${chipOf(BATCH_STATUS, settled.status).label.toLowerCase()}.`} />
      )}
      <p className="text-sm text-gray-700">
        The batch now has {counts.created || 0} created, {counts.failed || 0} failed, {counts.ready || 0} ready and{' '}
        {counts.incomplete || 0} missing files.
      </p>
    </>
  )
}
SettledStep.propTypes = { settled: PropTypes.object.isRequired }

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {object}   props.run       useProcessRun().state
 * @param {object}   props.batch     the page's batch (GET /:batchId)
 * @param {boolean}  [props.busy]    the settings are being applied
 * @param {object}   [props.files]   the file grid's { unsaved, uploading } (filesHoldProcess)
 * @param {Function} props.onStart   Process pressed
 * @param {Function} props.onHide    hide a live run (it goes on)
 * @param {Function} props.onClose   close (confirm step, or a run's ending)
 * @param {Function} props.onAgain   a new confirm step (Try again / Process again)
 * @param {Function} props.onResync  apply the company's current settings
 * @param {Function} props.onOpenBilling
 * @param {Function} props.onUploadNew
 */
export default function ProcessModal({
  open,
  run,
  batch,
  busy = false,
  files = null,
  onStart,
  onHide,
  onClose,
  onAgain,
  onResync,
  onOpenBilling,
  onUploadNew,
}) {
  if (!open || !batch) return null
  const { phase } = run
  const live = isLivePhase(phase)
  const title =
    phase === 'settled'
      ? run.settled?.stale
        ? 'Processing stopped before it finished'
        : 'Processing finished'
      : TITLES[phase] || 'Process'
  const shownCounts = phase === 'done' || phase === 'stopped' ? run.summary || run.counts : run.counts
  const direct = (run.start?.direct_ready || 0) > 0
  const ranRows = run.start !== null

  let body
  if (phase === 'confirm') {
    body = (
      <ConfirmStep
        batch={batch}
        checking={run.checking}
        recheckFailed={run.recheckFailed}
        busy={busy}
        files={files}
        onResync={onResync}
        onUploadNew={onUploadNew}
      />
    )
  } else if (phase === 'starting') {
    body = <Checking text="Checking the batch and starting…" />
  } else if (phase === 'refused') {
    body = <RefusedStep refusal={run.refusal} busy={busy} onResync={onResync} onUploadNew={onUploadNew} />
  } else if (phase === 'settled') {
    body = <SettledStep settled={run.settled} />
  } else {
    body = (
      <>
        {phase === 'stopped' && (
          <Notice tone="warning" title="Stopped early">
            <p>{run.stop?.message}</p>
          </Notice>
        )}
        {phase === 'failed' && (
          <Notice tone="error" title="The run broke off">
            <p>{run.failure?.message || 'Processing stopped unexpectedly.'}</p>
            <p>Rows already created stay created; the batch was read again.</p>
          </Notice>
        )}
        {phase === 'watching' && (
          <>
            <Notice tone="info" title="Following the run">
              <p>{WATCH_TEXT[run.watch] || WATCH_TEXT.connection_lost}</p>
              {run.processing?.started_at && <p>Started {formatDateTime(run.processing.started_at)}.</p>}
            </Notice>
            <Checking text="Checking the batch every few seconds…" />
          </>
        )}
        {ranRows && phase === 'streaming' && <RunProgress current={run.current} total={run.total} />}
        {(ranRows || phase === 'done' || phase === 'stopped') && <RunCounts counts={shownCounts} direct={direct} />}
        {(phase === 'done' || phase === 'stopped') && run.summary && <AfterRun summary={run.summary} />}
        <RunLog log={run.log} />
        {phase === 'streaming' && (
          <p className="text-xs text-gray-500">You can hide this window: processing goes on on the server.</p>
        )}
      </>
    )
  }

  const ready = batch.counts?.ready || 0
  const canStart = phase === 'confirm' && !run.checking && batch.can_process && !busy && !filesHoldProcess(files)
  const billing =
    (phase === 'stopped' && run.stop?.code === 'no_subscription') ||
    (phase === 'refused' && run.refusal?.code === 'no_subscription')
  const again =
    (phase === 'refused' && run.refusal?.retry) || (phase === 'failed' && batch.can_process)

  return (
    <MyModal open onClose={live ? null : onClose} maxWidth={600} forceBlur>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-process-title"
        className="flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-6 py-4">
          <div className="flex min-w-0 flex-col gap-0.5">
            <h2 id="bulk-process-title" className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
            <p className="truncate text-sm text-gray-500">
              {batch.batch_no} · {batch.company?.name || '—'}
            </p>
          </div>
          {!live && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close window"
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
            >
              <XClose className="size-5" stroke="currentColor" />
            </button>
          )}
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">{body}</div>

        <footer className="flex flex-wrap justify-end gap-3 border-t border-gray-200 px-6 py-4">
          {phase === 'confirm' && (
            <>
              <MyButton type="button" color="secondary" variant="outlined" size="md" onClick={onClose}>
                Back
              </MyButton>
              <MyButton type="button" color="primary" variant="filled" size="md" disabled={!canStart} onClick={onStart}>
                {ready > 0 ? `Process ${plural(ready, 'row')}` : 'Process'}
              </MyButton>
            </>
          )}
          {live && (
            <MyButton type="button" color="secondary" variant="outlined" size="md" onClick={onHide}>
              Hide
            </MyButton>
          )}
          {!live && phase !== 'confirm' && (
            <>
              {billing && (
                <MyButton type="button" color="secondary" variant="outlined" size="md" onClick={onOpenBilling}>
                  Open billing
                </MyButton>
              )}
              {again && (
                <MyButton type="button" color="secondary" variant="outlined" size="md" onClick={onAgain}>
                  {phase === 'failed' ? 'Process again' : 'Try again'}
                </MyButton>
              )}
              <MyButton type="button" color="primary" variant="filled" size="md" onClick={onClose}>
                Close
              </MyButton>
            </>
          )}
        </footer>
      </div>
    </MyModal>
  )
}

ProcessModal.propTypes = {
  open: PropTypes.bool.isRequired,
  run: PropTypes.object.isRequired,
  batch: PropTypes.object,
  busy: PropTypes.bool,
  files: PropTypes.shape({ unsaved: PropTypes.number, uploading: PropTypes.bool }),
  onStart: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onAgain: PropTypes.func.isRequired,
  onResync: PropTypes.func.isRequired,
  onOpenBilling: PropTypes.func.isRequired,
  onUploadNew: PropTypes.func.isRequired,
}
